import {
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSearch,
  FaSync,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import Card from "./Card";
import Button from "./Button";
import Input from "./Input";

export interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => ReactNode;
  sortable?: boolean;
  className?: string;
}

export interface DataTableFilter {
  key: string;
  label: string;
  type: "text" | "select" | "date";
  options?: { label: string; value: string }[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export interface DataTableFetchParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
  search?: string;
  filters?: Record<string, any>;
}

export interface DataTableFilterDef {
  key: string;
  label: string;
  type: "text" | "select" | "date";
  options?: { label: string; value: string }[];
  placeholder?: string;
  defaultValue?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data?: T[];
  loading?: boolean;
  pagination?: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
  sorting?: {
    sortBy: string;
    sortOrder: "ASC" | "DESC";
    onSort: (field: string) => void;
  };
  filters?: DataTableFilter[];
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  actions?: ReactNode;
  emptyMessage?: string;

  onFetch?: (
    params: DataTableFetchParams,
  ) => Promise<{ data: T[]; total: number; totalPages?: number }>;
  filterDefs?: DataTableFilterDef[];
  initialState?: {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
    search?: string;
    filters?: Record<string, any>;
  };

  refreshKey?: unknown;
  enableSearch?: boolean;
  enableSorting?: boolean;
  enablePagination?: boolean;
  enableRefresh?: boolean;
  pageSizeOptions?: number[];
  className?: string;
}

const SortIcon = ({
  active,
  order,
}: {
  active: boolean;
  order: "ASC" | "DESC";
}) => {
  if (!active) return <FaSort className="ml-1 inline text-gray-400" />;
  return order === "ASC" ? (
    <FaSortUp className="ml-1 inline text-blue-500" />
  ) : (
    <FaSortDown className="ml-1 inline text-blue-500" />
  );
};

function DataTable<T extends { id: string | number }>({
  columns,
  data: controlledData,
  loading: controlledLoading = false,
  pagination: controlledPagination,
  sorting: controlledSorting,
  filters: controlledFilters,
  search: controlledSearch,
  actions,
  emptyMessage,
  onFetch,
  filterDefs,
  initialState,
  refreshKey,
  enableSearch: enableSearchProp,
  enableSorting: enableSortingProp,
  enablePagination: enablePaginationProp,
  enableRefresh: enableRefreshProp,
  className = "",
}: DataTableProps<T>) {
  const { t } = useTranslation("common");

  const [internalData, setInternalData] = useState<T[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalTotal, setInternalTotal] = useState(0);
  const [internalTotalPages, setInternalTotalPages] = useState(0);
  const [internalError, setInternalError] = useState<string>("");

  const [page, setPage] = useState(() => initialState?.page || 1);
  const [pageSize, setPageSize] = useState(() => initialState?.pageSize || 50);
  const [sortBy, setSortBy] = useState<string>(() => {
    if (initialState?.sortBy) return initialState.sortBy;
    const firstSortable = columns.find((c) => c.sortable)?.key;
    return firstSortable || "";
  });
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">(
    initialState?.sortOrder || "DESC",
  );
  const [search, setSearch] = useState(() => initialState?.search || "");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [filterValues, setFilterValues] = useState<Record<string, any>>(
    initialState?.filters || {},
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  const filtersButtonRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!filtersOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        filtersRef.current &&
        !filtersRef.current.contains(target) &&
        filtersButtonRef.current &&
        !filtersButtonRef.current.contains(target)
      ) {
        setFiltersOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [filtersOpen]);

  useEffect(() => {
    if (!filterDefs) return;
    setFilterValues((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const defaults: Record<string, any> = {};
      filterDefs.forEach((def) => {
        if (def.defaultValue) defaults[def.key] = def.defaultValue;
      });
      if (Object.keys(defaults).length === 0) return prev;
      return defaults;
    });
  }, [filterDefs]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  const isServerMode = !!onFetch;
  const isClientMode = !onFetch;

  const enableSearch = enableSearchProp ?? true;
  const enableSorting = enableSortingProp ?? true;
  const enablePagination = enablePaginationProp ?? true;
  const enableRefresh = enableRefreshProp ?? true;

  const fetchData = useCallback(async () => {
    if (!onFetch) return;

    setInternalLoading(true);
    setInternalError("");
    try {
      const result = await onFetch({
        page,
        pageSize,
        sortBy,
        sortOrder,
        search: debouncedSearch,
        filters: filterValues,
      });
      setInternalData(result.data);
      setInternalTotal(result.total);
      if (result.totalPages) {
        setInternalTotalPages(result.totalPages);
      } else {
        setInternalTotalPages(Math.ceil(result.total / pageSize));
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Failed to load data";
      setInternalError(message);
    } finally {
      setInternalLoading(false);
    }
  }, [
    onFetch,
    page,
    pageSize,
    sortBy,
    sortOrder,
    debouncedSearch,
    filterValues,
  ]);

  useEffect(() => {
    if (onFetch) {
      fetchData();
    }
  }, [fetchData, onFetch, refreshKey]);

  const isPaginationControlled = !!controlledPagination;
  const isSortingControlled = !!controlledSorting;
  const isSearchControlled = !!controlledSearch;
  const isFiltersControlled = !!controlledFilters;

  useEffect(() => {
    if (!enablePagination) return;
    if (isPaginationControlled) return;
    setPage(1);
  }, [
    debouncedSearch,
    filterValues,
    sortBy,
    sortOrder,
    enablePagination,
    isPaginationControlled,
  ]);

  const clientProcessedData = useMemo(() => {
    if (!isClientMode)
      return { pageData: [] as T[], totalItems: 0, totalPages: 0 };

    let result = controlledData ? [...controlledData] : [];

    const searchTerm = debouncedSearch.trim().toLowerCase();
    if (enableSearch && searchTerm) {
      result = result.filter((row) => {
        return columns.some((col) => {
          const value = (row as any)[col.key] as unknown;
          if (value === null || value === undefined) return false;
          if (
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
          ) {
            return String(value).toLowerCase().includes(searchTerm);
          }
          return false;
        });
      });
    }

    if (filterDefs && filterDefs.length > 0) {
      result = result.filter((row) => {
        return filterDefs.every((def) => {
          const filterValue = filterValues[def.key];
          if (
            filterValue === null ||
            filterValue === undefined ||
            String(filterValue).trim() === ""
          ) {
            return true;
          }
          const rowValue = (row as any)[def.key] as unknown;
          if (rowValue === null || rowValue === undefined) return false;

          if (def.type === "select") {
            return String(rowValue) === String(filterValue);
          }

          if (def.type === "date") {
            const rowDate =
              typeof rowValue === "string"
                ? rowValue.slice(0, 10)
                : String(rowValue).slice(0, 10);
            return rowDate === String(filterValue).slice(0, 10);
          }

          return String(rowValue)
            .toLowerCase()
            .includes(String(filterValue).toLowerCase());
        });
      });
    }

    if (enableSorting && sortBy) {
      result.sort((a, b) => {
        const aValue = (a as any)[sortBy] as unknown;
        const bValue = (b as any)[sortBy] as unknown;
        const aComparable =
          aValue === null || aValue === undefined ? "" : aValue;
        const bComparable =
          bValue === null || bValue === undefined ? "" : bValue;
        if (aComparable === bComparable) return 0;
        if (sortOrder === "ASC") {
          return aComparable > bComparable ? 1 : -1;
        }
        return aComparable < bComparable ? 1 : -1;
      });
    }

    const totalItems = result.length;
    const totalPages = enablePagination
      ? Math.max(1, Math.ceil(totalItems / pageSize))
      : 1;

    const safePage = enablePagination
      ? Math.min(Math.max(1, page), totalPages)
      : 1;
    const start = enablePagination ? (safePage - 1) * pageSize : 0;
    const pageData = enablePagination
      ? result.slice(start, start + pageSize)
      : result;

    return { pageData, totalItems, totalPages };
  }, [
    isClientMode,
    controlledData,
    columns,
    debouncedSearch,
    enableSearch,
    enableSorting,
    enablePagination,
    filterDefs,
    filterValues,
    page,
    pageSize,
    sortBy,
    sortOrder,
  ]);

  const activeLoading = isServerMode ? internalLoading : controlledLoading;

  const activeData = isServerMode
    ? internalData
    : isPaginationControlled
      ? controlledData || []
      : clientProcessedData.pageData;

  const activePagination = !enablePagination
    ? undefined
    : isPaginationControlled
      ? controlledPagination
      : isServerMode
        ? {
            page,
            pageSize,
            totalItems: internalTotal,
            totalPages: internalTotalPages,
            onPageChange: (nextPage: number) => setPage(nextPage),
            onPageSizeChange: (nextSize: number) => {
              setPageSize(nextSize);
              setPage(1);
            },
          }
        : {
            page,
            pageSize,
            totalItems: clientProcessedData.totalItems,
            totalPages: clientProcessedData.totalPages,
            onPageChange: (nextPage: number) => setPage(nextPage),
            onPageSizeChange: (nextSize: number) => {
              setPageSize(nextSize);
              setPage(1);
            },
          };

  const activeSorting = !enableSorting
    ? undefined
    : isSortingControlled
      ? controlledSorting
      : {
          sortBy,
          sortOrder,
          onSort: (key: string) => {
            if (sortBy === key) {
              setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
            } else {
              setSortBy(key);
              setSortOrder("ASC");
            }
          },
        };

  const activeSearch = !enableSearch
    ? undefined
    : isSearchControlled
      ? controlledSearch
      : {
          value: search,
          onChange: setSearch,
          placeholder: t("search"),
        };

  const activeFilters: DataTableFilter[] | undefined = isFiltersControlled
    ? controlledFilters
    : filterDefs?.map((def) => ({
        key: def.key,
        label: def.label,
        type: def.type,
        options: def.options,
        placeholder: def.placeholder,
        value: filterValues[def.key],
        onChange: (val: string) =>
          setFilterValues((prev: Record<string, any>) => ({
            ...prev,
            [def.key]: val,
          })),
      }));

  const handleSort = (key: string) => {
    if (activeSorting && activeSorting.onSort) {
      activeSorting.onSort(key);
    }
  };

  const hasToolbar =
    activeSearch ||
    (activeFilters && activeFilters.length > 0) ||
    actions ||
    (isServerMode && enableRefresh);

  return (
    <div className={`space-y-4 flex flex-col ${className}`}>
      <Card className="shadow-md border-0 flex-1 flex flex-col overflow-hidden">
        {hasToolbar && (
          <div className="p-4 flex-shrink-0">
            <div className="flex flex-col md:flex-row justify-between gap-4 items-start md:items-center">
              <div className="flex flex-col gap-4 flex-1 w-full">
                {activeSearch && (
                  <div className="relative w-full md:w-80">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="text-gray-400" />
                    </div>
                    <Input
                      className="pl-10"
                      placeholder={activeSearch.placeholder || t("search")}
                      value={activeSearch.value}
                      onChange={(e) => activeSearch.onChange(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="relative flex gap-2">
                {activeFilters && activeFilters.length > 0 && (
                  <span ref={filtersButtonRef}>
                    <Button
                      variant="secondary"
                      aria-label={t("filters")}
                      onClick={() => setFiltersOpen((prev) => !prev)}
                    >
                      <FaFilter />
                    </Button>
                  </span>
                )}
                {isServerMode && enableRefresh && (
                  <Button
                    variant="secondary"
                    onClick={() => fetchData()}
                    disabled={internalLoading}
                    aria-label={t("refresh")}
                  >
                    <FaSync className={internalLoading ? "animate-spin" : ""} />
                  </Button>
                )}
                {actions}
                {activeFilters && activeFilters.length > 0 && filtersOpen && (
                  <div
                    ref={filtersRef}
                    className="absolute right-0 top-full mt-2 z-20 w-[50vw] max-w-md"
                  >
                    <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                          {t("filters")}
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          aria-label={t("clear")}
                          onClick={() =>
                            setFilterValues((prev: Record<string, any>) => {
                              const next = { ...prev };
                              if (activeFilters) {
                                activeFilters.forEach((f) => {
                                  next[f.key] = "";
                                });
                              }
                              return next;
                            })
                          }
                        >
                          {t("clear")}
                        </Button>
                      </div>
                      <div className="p-3 max-h-[60vh] overflow-auto themed-scrollbar themed-scrollbar-compact">
                        <div className="grid grid-cols-1 gap-4">
                          {activeFilters.map((filter) => (
                            <div key={filter.key} className="w-full">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                                {filter.label}
                              </label>
                              {filter.type === "select" ? (
                                <select
                                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  value={filter.value || ""}
                                  onChange={(e) =>
                                    filter.onChange(e.target.value)
                                  }
                                >
                                  <option value="">{t("all")}</option>
                                  {filter.options?.map((opt) => (
                                    <option
                                      key={opt.value}
                                      value={opt.value}
                                      className="break-words"
                                    >
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <Input
                                  placeholder={
                                    filter.placeholder || t("search")
                                  }
                                  value={filter.value || ""}
                                  onChange={(e) =>
                                    filter.onChange(e.target.value)
                                  }
                                  type={filter.type}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {isServerMode && internalError && (
          <div className="px-4">
            <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100">
              {internalError}
            </div>
          </div>
        )}
        <div className="border-t border-gray-200 dark:border-gray-700 flex-shrink-0" />
        <div className="flex-1 overflow-auto themed-scrollbar themed-scrollbar-prominent min-h-0">
          <table className="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10 shadow-sm">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`text-left px-6 py-4 font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider text-xs ${
                      col.sortable && activeSorting
                        ? "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        : ""
                    } ${col.className || ""}`}
                    onClick={() =>
                      col.sortable && activeSorting && handleSort(col.key)
                    }
                  >
                    <div className="flex items-center">
                      {col.header}
                      {col.sortable && activeSorting && (
                        <SortIcon
                          active={activeSorting.sortBy === col.key}
                          order={activeSorting.sortOrder}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {activeLoading ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <span className="ml-2">{t("loading")}...</span>
                    </div>
                  </td>
                </tr>
              ) : activeData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    {emptyMessage || t("noData")}
                  </td>
                </tr>
              ) : (
                activeData.map((item, idx) => (
                  <tr
                    key={item.id}
                    className={`transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800 ${idx % 2 === 0 ? "bg-white dark:bg-gray-900" : "bg-gray-50 dark:bg-gray-800"}`}
                  >
                    {columns.map((col) => {
                      const isActions = col.key === "actions";
                      return (
                        <td
                          key={`${item.id}-${col.key}`}
                          className={`px-6 py-4 align-middle ${
                            isActions
                              ? "whitespace-normal"
                              : "whitespace-nowrap"
                          } ${col.className || "text-gray-500 dark:text-gray-400"}`}
                        >
                          <div
                            className={
                              isActions
                                ? "flex flex-wrap items-center justify-start gap-2"
                                : ""
                            }
                          >
                            {col.render
                              ? col.render(item)
                              : (item as any)[col.key]}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {activePagination && (
          <div className="bg-gray-50 dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium text-gray-900 dark:text-white">
                {activeData.length}
              </span>{" "}
              {t("of")}{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                {activePagination.totalItems}
              </span>{" "}
              {t("results")}
            </div>
            <div className="flex items-center gap-2">
              <select
                className="rounded-md border border-gray-300 dark:border-gray-600 px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                value={activePagination.pageSize}
                onChange={(e) =>
                  activePagination.onPageSizeChange(Number(e.target.value))
                }
              >
                {[10, 25, 50, 100].map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  activePagination.onPageChange(
                    Math.max(1, activePagination.page - 1),
                  )
                }
                disabled={activePagination.page === 1}
                aria-label={t("previous")}
              >
                <FaChevronLeft />
              </Button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {activePagination.page} / {activePagination.totalPages || 1}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  activePagination.onPageChange(
                    activePagination.totalPages
                      ? Math.min(
                          activePagination.totalPages,
                          activePagination.page + 1,
                        )
                      : activePagination.page + 1,
                  )
                }
                disabled={
                  activePagination.totalPages
                    ? activePagination.page >= activePagination.totalPages
                    : false
                }
                aria-label={t("next")}
              >
                <FaChevronRight />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export default DataTable;
